import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { Search } from "@/components/search/Search.tsx";
import useDataProtectionHook from "@/hooks/use-data-protection.hook.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ApplicantDataProtectionOption } from "@/pages/recruit/candidates/components/ApplicantDataProtectionOption.tsx";
import { useDataProtectionSearchRequests } from "@/queries/use-data-protection.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { Rounding, ColumnSizes } from "@/types/common.types.ts";
import {
  ApiApplicantDataProtection,
  ApiDataProtectionRequestItem,
  ApiDataProtectionRequestsFilter,
  DataProtectionState,
} from "@/types/data-protection.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Avatar,
  DropdownMenu,
  LinkTo,
  PaginationBlock,
  Stack,
  Table,
  Text,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce, useEffectOnce } from "react-use";

export function DataProtectionTabRequests() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<ApiDataProtectionRequestsFilter>({
    text: "",
    page_number: 1,
    states: [
      DataProtectionState.requested,
      DataProtectionState.given,
      DataProtectionState.expired,
      DataProtectionState.expiring,
      DataProtectionState.declined,
    ],
  });
  const { data: requests, isLoading, isFetching } = useDataProtectionSearchRequests(filter);
  const { canIEdit } = usePermissions();
  const { getTitleByType } = useDataProtectionHook();
  const { renderRelativeDate, getDaysToDate } = useFormatter();

  useDebounce(
    () => {
      setFilter({
        ...filter,
        page_number: 1,
        text: searchTerm,
      });
    },
    300,
    [searchTerm]
  );

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.dataProtection.viewed, {
      [analyticProperties.tab]: "Requests",
    });
  });

  const getStateElement = (dataProtection: ApiApplicantDataProtection): ReactNode => {
    const title = getTitleByType(dataProtection.type);
    switch (dataProtection.state) {
      case DataProtectionState.requested:
        return <Text>{`${title} - ${t("data_protection:consent_requested")}`}</Text>;
      case DataProtectionState.given:
        return (
          <Text
            className={"text-positive"}
          >{`${title} - ${t("data_protection:consent_received")}`}</Text>
        );
      case DataProtectionState.expired:
        return (
          <Text
            className={"text-danger"}
          >{`${title} - ${t("data_protection:consent_expired")}`}</Text>
        );
      case DataProtectionState.expiring:
        return (
          <Text
            className={"text-warning"}
          >{`${title} - ${t("data_protection:consent_expiring")}`}</Text>
        );
      case DataProtectionState.declined:
        return (
          <Text
            className={"text-danger"}
          >{`${title} - ${t("data_protection:consent_declined")}`}</Text>
        );
    }
  };

  const getRetentionPeriod = (dataProtection: ApiApplicantDataProtection): string => {
    if (
      dataProtection.state !== DataProtectionState.given &&
      dataProtection.state !== DataProtectionState.expiring
    )
      return "-";

    return t("data_protection:status_request_given", {
      count_days: t("plurals:plural_day", {
        count: getDaysToDate(dataProtection.datetime_state_end, Rounding.up),
      }),
    });
  };

  const columns = useMemo<ColumnDef<ApiDataProtectionRequestItem>[]>(
    () => [
      {
        header: t("common:sent"),
        cell: (row) =>
          row.row.original.data_protection.datetime_sent
            ? renderRelativeDate(row.row.original.data_protection.datetime_sent)
            : "",
        accessorKey: "sent",
        ...ColumnSizes.date,
      },
      {
        header: t("ats:candidate"),
        cell: (row) => (
          <LinkTo to={`/recruitment/candidates/${row.row.original.id}/`} className={"no-underline"}>
            <Stack
              direction={"horizontal"}
              className={"text-ellipsis whitespace-nowrap"}
              gap={"sm"}
            >
              <Avatar size={"md"} url={row.row.original.photo_url} />
              <Stack direction={"vertical"}>
                <Text
                  className={
                    "text-ellipsis whitespace-nowrap max-w-32 overflow-hidden font-semibold text-dark"
                  }
                >{`${row.row.original.first_name} ${row.row.original.last_name}`}</Text>
                {getStateElement(row.row.original.data_protection)}
              </Stack>
            </Stack>
          </LinkTo>
        ),
        accessorKey: "candidate",
        ...ColumnSizes.employee,
      },
      {
        header: t("data_protection:remaining_consent_period"),
        cell: (row) => getRetentionPeriod(row.row.original.data_protection),
        accessorKey: "retention",
        ...ColumnSizes.standard,
      },
    ],
    []
  );

  const dataTransform = useMemo<ApiDataProtectionRequestItem[]>(() => {
    const result: ApiDataProtectionRequestItem[] = [];

    requests?.items.forEach((request) => {
      request.data_protection.forEach((dataProtectionItem) => {
        result.push({
          ...request,
          data_protection: dataProtectionItem,
        });
      });
    });

    return result;
  }, [requests]);

  const renderRowAction = useCallback(
    (row: Row<ApiDataProtectionRequestItem>) =>
      canIEdit(ApiPermission.manage_candidates) ? (
        <DropdownMenu>
          <ApplicantDataProtectionOption
            dataProtection={row.original.data_protection}
            applicantId={row.original.id}
            key={`protection-${row.original.data_protection.type}`}
          />
        </DropdownMenu>
      ) : null,
    [canIEdit]
  );

  return (
    <Stack>
      <Search
        placeholder={t("core:name")}
        value={searchTerm}
        onInput={(e) => {
          setSearchTerm(e.currentTarget.value);
        }}
        type={"text"}
      />
      <Table
        renderRowAction={renderRowAction}
        data={dataTransform || []}
        columns={columns}
        showSkeleton={isLoading}
        isFetching={isFetching}
        showNotFoundError={filter.text != "" && requests?.metadata.total_items == 0}
        notFoundComponent={<EmptyStateNothingFound />}
        emptyStateComponent={<EmptyState />}
      />
      <PaginationBlock
        metadata={requests?.metadata}
        onPageChange={(page) => {
          setFilter({ ...filter, page_number: page });
        }}
      />
    </Stack>
  );
}
